import { Suspense, useContext, useEffect, useState } from "react";
import { Navigate, Route, Routes as RRDRoutes, useLocation } from "react-router-dom";
import ErrorBoundary from "../components/ErrorBoundary";
import PhoneNumberModal from "../components/Forms/PhoneNumberModal";
import MainSidebar from "../components/Layout/Sidebar";
import PartnerTheme from "../components/Layout/PartnerTheme";
import AdminSidebar from "../components/Layout/Sidebar/AdminSidebar";
import { MainTopbar } from "../components/Layout/Topbar";
import { Loader } from "../components/Loader";
import { SidebarContext } from "../contexts/SidebarContext";
// ============================================================================
// STATIC IMPORTS - Essential pages loaded immediately
// ============================================================================
// Auth pages (needed for initial load)
import Login from "../pages/Auth/Login/Login";
import { RecoveryNewPassword } from "../pages/Auth/Recovery/RecoveryNewPassword";
import { RecoveryRequest } from "../pages/Auth/Recovery/RecoveryRequest";
import SignUp from "../pages/Auth/SignUp/SignUp";
import { AuthContext } from "../providers/AuthProvider";
import { getDemoAssistantData } from "../utils/demoAssistant";
import lazyWithRetry from "../utils/lazyWithRetry";

// ============================================================================
// LAZY IMPORTS - Code splitting for better performance
// ============================================================================

// Auth pages (less frequently accessed)
const InstagramCallback = lazyWithRetry(
  () => import("../pages/Auth/InstagramCallback/InstagramCallback"),
);
const SignUpSales = lazyWithRetry(() => import("../pages/Auth/SignUpSales"));
const ThankYou = lazyWithRetry(() => import("../pages/Auth/ThankYou"));
const CompleteProfile = lazyWithRetry(() => import("../pages/Auth/CompleteProfile"));
const AccountEfetivation = lazyWithRetry(() =>
  import("../pages/Auth/AccountEfetivation/index.js").then((m) => ({
    default: m.AccountEfetivation,
  })),
);
const SucessScreen = lazyWithRetry(
  () => import("../pages/Auth/AccountEfetivation/SucessScreen/index.js"),
);

// Dashboard pages
const Dashboard = lazyWithRetry(() => import("../pages/Dashboard/Partner/Dashboard"));
const AdminDashboard = lazyWithRetry(() => import("../pages/Dashboard/Admin/AdminDashboard"));
const DashboardSales = lazyWithRetry(() => import("../pages/Dashboard/Sales/Dashboard"));
const AttendantDashboard = lazyWithRetry(
  () => import("../pages/Conversations/ConversationsPage"),
);

// Clients pages
const ChatHistory = lazyWithRetry(() => import("../pages/Clients/ChatHistory/ChatHistory"));

// Settings pages
const AccountShadcn = lazyWithRetry(() => import("../pages/Settings/Account"));

// Finance pages
const FinancePartner = lazyWithRetry(() => import("../pages/Finance/Overview/index"));
const NewCreditCard = lazyWithRetry(() => import("../pages/Finance/Overview/NewCreditCard.js"));
const Step1 = lazyWithRetry(() => import("../pages/Finance/Overview/Step1/Step1.js"));
const Step2 = lazyWithRetry(() => import("../pages/Finance/Overview/Step2/Step2.js"));
const Step3 = lazyWithRetry(() => import("../pages/Finance/Overview/Step3/Step3.js"));
const SubscriptionShadcn = lazyWithRetry(() =>
  import("../pages/Finance/Subscription/index").then((m) => ({ default: m.SubscriptionShadcn })),
);
const CheckoutShadcn = lazyWithRetry(() => import("../pages/Finance/Checkout/index"));
const TokenPurchaseStepper = lazyWithRetry(
  () => import("../pages/Finance/Tokens/TokenPurchaseStepper"),
);
const SubscribePage = lazyWithRetry(() => import("../pages/Finance/Subscribe/index"));

// Automation pages
const FlowList = lazyWithRetry(() => import("../pages/Automation/Flows"));
const FlowEditor = lazyWithRetry(() => import("../pages/Automation/Flows/FlowEditor"));
const FlowMetrics = lazyWithRetry(() => import("../pages/Automation/Flows/FlowMetrics"));

// Access pages
const AccessManagement = lazyWithRetry(() => import("../pages/Access/Management"));

// Support pages
const Support = lazyWithRetry(() => import("../pages/Support"));

// Assistants management page
const OfficePage = lazyWithRetry(() => import("../pages/Office/OfficePage"));
const MessagingPage = lazyWithRetry(() => import("../pages/Office/MessagingPage"));
const StatisticsPage = lazyWithRetry(() => import("../pages/Statistics/StatisticsPage"));

// Interview page - Onboarding imersivo passo a passo
const InterviewPage = lazyWithRetry(() => import("../pages/Interview/InterviewPage"));

// Demo page
const DemoPage = lazyWithRetry(() => import("../pages/Demo/DemoPage"));
const DemoProvisioning = lazyWithRetry(() => import("../pages/Demo/DemoProvisioning"));
const DemoThankYou = lazyWithRetry(() => import("../pages/Demo/DemoThankYou"));
const ChoosePlan = lazyWithRetry(() => import("../pages/Onboarding/ChoosePlan"));
const PlanCheckout = lazyWithRetry(() => import("../pages/Onboarding/PlanCheckout"));

// Admin pages
const PartnerList = lazyWithRetry(() => import("../pages/Admin/Partners/PartnerList"));
const PartnerClientsList = lazyWithRetry(
  () => import("../pages/Admin/PartnersClients/PartnerClientsList"),
);
const AdminChatHistory = lazyWithRetry(
  () => import("../pages/Admin/PartnersClients/ChatHistory/AdminChatHistory"),
);
const Finance = lazyWithRetry(() => import("../pages/Admin/Finance"));
const AdminAffiliates = lazyWithRetry(() => import("../pages/Admin/Affiliates"));
const AdminUsers = lazyWithRetry(() => import("../pages/Admin/Users"));
const SchedulesList = lazyWithRetry(() =>
  import("../pages/Admin/Schedules").then((m) => ({ default: m.SchedulesList })),
);
const FollowUpsList = lazyWithRetry(() =>
  import("../pages/Admin/FollowUps").then((m) => ({ default: m.FollowUpsList })),
);

// Sales pages
const AccountSales = lazyWithRetry(() => import("../pages/Sales/Account/Profile-Premium"));
const CRM = lazyWithRetry(() => import("../pages/Sales/CRM").then((m) => ({ default: m.CRM })));
const ClientListSales = lazyWithRetry(() => import("../pages/Sales/ClientList/Clients"));
const SalesNewCreditCard = lazyWithRetry(() => import("../pages/Sales/Finance/NewCreditCard"));
const SalesTokens = lazyWithRetry(() => import("../pages/Sales/Tokens/Tokens"));
const SalesCheckout = lazyWithRetry(() => import("../pages/Sales/Checkout/Checkout"));
const SalesSuport = lazyWithRetry(() => import("../pages/Sales/Support/Support-Premium"));

// Development-only imports
const TestWebSocket =
  process.env.NODE_ENV === "development"
    ? lazyWithRetry(() => import("../pages/_dev/TestWebSocket"))
    : null;
const TestAttendantWebSocket =
  process.env.NODE_ENV === "development"
    ? lazyWithRetry(() => import("../pages/_dev/TestAttendantWebSocket"))
    : null;

// ============================================================================
// LOADING FALLBACK COMPONENT
// ============================================================================
const PageLoader = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      width: "100%",
    }}
  >
    <Loader size={48} />
  </div>
);

// ============================================================================
// LAZY WRAPPER - Wraps lazy components with Suspense
// ============================================================================
const LazyRoute = ({ children }) => <Suspense fallback={<PageLoader />}>{children}</Suspense>;

// ============================================================================
// ROUTES COMPONENT
// ============================================================================
export const Routes = () => {
  if (window.location.protocol === "http:" && window.location.hostname !== "localhost") {
    window.location.href = window.location.href.replace("http:", "https:");
    return null;
  }

  return (
    <RRDRoutes>
      {/* SALES ROUTES */}
      <Route
        path="/vendas/conta"
        element={
          <SalesRoute>
            <LazyRoute>
              <AccountSales />
            </LazyRoute>
          </SalesRoute>
        }
      />
      <Route
        path="/vendas/dashboard"
        element={
          <SalesRoute>
            <LazyRoute>
              <DashboardSales />
            </LazyRoute>
          </SalesRoute>
        }
      />
      <Route
        path="/vendas/crm"
        element={
          <SalesRoute>
            <LazyRoute>
              <CRM />
            </LazyRoute>
          </SalesRoute>
        }
      />
      <Route
        path="/vendas/clientes"
        element={
          <SalesRoute>
            <LazyRoute>
              <ClientListSales />
            </LazyRoute>
          </SalesRoute>
        }
      />
      <Route
        path="/vendas/financeiro/cartao-de-credito"
        element={
          <SalesRoute>
            <LazyRoute>
              <SalesNewCreditCard />
            </LazyRoute>
          </SalesRoute>
        }
      />
      <Route
        path="/vendas/tokens"
        element={
          <SalesRoute>
            <LazyRoute>
              <SalesTokens />
            </LazyRoute>
          </SalesRoute>
        }
      />
      <Route
        path="/vendas/carrinho"
        element={
          <SalesRoute>
            <LazyRoute>
              <SalesCheckout />
            </LazyRoute>
          </SalesRoute>
        }
      />
      <Route
        path="/vendas/suporte"
        element={
          <SalesRoute>
            <LazyRoute>
              <SalesSuport />
            </LazyRoute>
          </SalesRoute>
        }
      />

      {/* PARTNER ROUTES */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <ErrorBoundary>
              <LazyRoute>
                <Dashboard />
              </LazyRoute>
            </ErrorBoundary>
          </PrivateRoute>
        }
      />
      <Route
        path="/entrevista/:assistantId"
        element={
          <PrivateRoute>
            <ErrorBoundary>
              <LazyRoute>
                <InterviewPage />
              </LazyRoute>
            </ErrorBoundary>
          </PrivateRoute>
        }
      />
      <Route
        path="/conversas"
        element={
          <PrivateRoute>
            <ErrorBoundary>
              <LazyRoute>
                <AttendantDashboard />
              </LazyRoute>
            </ErrorBoundary>
          </PrivateRoute>
        }
      />
      <Route path="/atendente" element={<Navigate to="/conversas" replace />} />
      <Route path="/clientes" element={<Navigate to="/conversas" replace />} />
      {/* rota legada — bookmarks de /onboarding redirecionam pro hub de assistentes */}
      <Route path="/onboarding" element={<Navigate to="/assistants" replace />} />
      {/* O Escritório — home 3D dos assistentes (fallback 2D embutido) */}
      <Route
        path="/assistants"
        element={
          <PrivateRoute>
            <LazyRoute>
              <OfficePage />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route path="/mensageria" element={<PrivateRoute><LazyRoute><MessagingPage /></LazyRoute></PrivateRoute>} />
      {/* Tela 15 Estatísticas (F7) — /dashboard legado continua roteável */}
      <Route path="/estatisticas" element={<PrivateRoute><ErrorBoundary><LazyRoute><StatisticsPage /></LazyRoute></ErrorBoundary></PrivateRoute>} />
      <Route path="/mensageria/:boxId" element={<PrivateRoute><LazyRoute><MessagingPage /></LazyRoute></PrivateRoute>} />
      {/* Rotas legadas das ferramentas do assistente (F1/F3): bookmarks redirecionam pro Escritório.
          Antes ficavam sob o layout antigo do assistente, que montava o simulador (GET bot_configs,
          partner_test_bot/messages e websocket) só para redirecionar. */}
      <Route path="/assistente" element={<Navigate to="/assistants" replace />} />
      <Route path="/conhecimento" element={<Navigate to="/assistants" replace />} />
      <Route path="/resposta-voz" element={<Navigate to="/assistants" replace />} />
      <Route path="/widget-web" element={<Navigate to="/assistants" replace />} />
      <Route path="/vincular" element={<Navigate to="/assistants" replace />} />
      <Route path="/agendamento" element={<Navigate to="/assistants" replace />} />
      <Route path="/configuracoes" element={<Navigate to="/assistants" replace />} />
      <Route path="/configuracoes/automatica" element={<Navigate to="/assistants" replace />} />
      <Route path="/follow-up" element={<Navigate to="/assistants" replace />} />
      <Route path="/pesquisas-satisfacao" element={<Navigate to="/assistants" replace />} />
      {/* Decidido 14/09 (M7): CRM (D9), simulador, transbordo por parceiro e resultados de pesquisa
          saem da primeira versão; /disparador (sem uso desde 03/2025) aponta para Disparos. */}
      <Route path="/pesquisas-satisfacao/resultados" element={<Navigate to="/assistants" replace />} />
      <Route path="/leads" element={<Navigate to="/assistants" replace />} />
      <Route path="/leads/metrics" element={<Navigate to="/assistants" replace />} />
      <Route path="/leads/settings" element={<Navigate to="/assistants" replace />} />
      <Route path="/simulador" element={<Navigate to="/assistants" replace />} />
      <Route path="/transbordo-humano" element={<Navigate to="/assistants" replace />} />
      <Route path="/disparador" element={<Navigate to="/flows" replace />} />
      <Route path="/disparador/create" element={<Navigate to="/flows" replace />} />
      <Route path="/disparador/edite/:id" element={<Navigate to="/flows" replace />} />
      <Route
        path="/conta"
        element={
          <PrivateRoute>
            <LazyRoute>
              <AccountShadcn />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      {/* Flows */}
      <Route
        path="/flows"
        element={
          <PrivateRoute>
            <LazyRoute>
              <FlowList />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/flows/editor"
        element={
          <PrivateRoute>
            <LazyRoute>
              <FlowEditor />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/flows/editor/:id"
        element={
          <PrivateRoute>
            <LazyRoute>
              <FlowEditor />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/flows/metrics/:id"
        element={
          <PrivateRoute>
            <LazyRoute>
              <FlowMetrics />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/acessos"
        element={
          <PrivateRoute>
            <LazyRoute>
              <AccessManagement />
            </LazyRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/suporte"
        element={
          <PrivateRoute>
            <LazyRoute>
              <Support />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/chat/:id"
        element={
          <PrivateRoute>
            <LazyRoute>
              <ChatHistory />
            </LazyRoute>
          </PrivateRoute>
        }
      />

      {/* Finance */}
      <Route
        path="/financeiro"
        element={
          <PrivateRoute>
            <LazyRoute>
              <FinancePartner />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/financeiro/cartao-de-credito"
        element={
          <PrivateRoute>
            <LazyRoute>
              <NewCreditCard />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/financeiro/step1"
        element={
          <PrivateRoute>
            <LazyRoute>
              <Step1 />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/financeiro/step2"
        element={
          <PrivateRoute>
            <LazyRoute>
              <Step2 />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/financeiro/step3"
        element={
          <PrivateRoute>
            <LazyRoute>
              <Step3 />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/planos"
        element={
          <PrivateRoute>
            <LazyRoute>
              <SubscriptionShadcn />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/tokens"
        element={
          <PrivateRoute>
            <LazyRoute>
              <TokenPurchaseStepper />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/planos/:hash"
        element={
          <PrivateRoute>
            <LazyRoute>
              <SubscriptionShadcn />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/carrinho"
        element={
          <PrivateRoute>
            <LazyRoute>
              <CheckoutShadcn />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/assinar"
        element={
          <PrivateRoute>
            <LazyRoute>
              <SubscribePage />
            </LazyRoute>
          </PrivateRoute>
        }
      />

      {/* Test WebSocket Routes - Development Only */}
      {process.env.NODE_ENV === "development" && TestWebSocket && (
        <Route
          path="/test-websocket"
          element={
            <PrivateRoute>
              <LazyRoute>
                <TestWebSocket />
              </LazyRoute>
            </PrivateRoute>
          }
        />
      )}
      {process.env.NODE_ENV === "development" && TestAttendantWebSocket && (
        <Route
          path="/test-attendant-websocket"
          element={
            <PrivateRoute>
              <LazyRoute>
                <TestAttendantWebSocket />
              </LazyRoute>
            </PrivateRoute>
          }
        />
      )}

      {/* ADMIN ROUTES */}
      <Route
        path="/admin/dashboard"
        element={
          <AdminRoute>
            <ErrorBoundary>
              <LazyRoute>
                <AdminDashboard />
              </LazyRoute>
            </ErrorBoundary>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/parceiros"
        element={
          <AdminRoute>
            <LazyRoute>
              <PartnerList />
            </LazyRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/parceiros/:id"
        element={
          <AdminRoute>
            <LazyRoute>
              <PartnerClientsList />
            </LazyRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/parceiros/:id/chat/:clientId"
        element={
          <AdminRoute>
            <LazyRoute>
              <AdminChatHistory />
            </LazyRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/financeiro"
        element={
          <AdminRoute>
            <LazyRoute>
              <Finance />
            </LazyRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/afiliados"
        element={
          <AdminRoute>
            <LazyRoute>
              <AdminAffiliates />
            </LazyRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/usuarios"
        element={
          <AdminRoute>
            <LazyRoute>
              <AdminUsers />
            </LazyRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/schedules"
        element={
          <AdminRoute>
            <LazyRoute>
              <SchedulesList />
            </LazyRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/follow-ups"
        element={
          <AdminRoute>
            <LazyRoute>
              <FollowUpsList />
            </LazyRoute>
          </AdminRoute>
        }
      />

      {/* PUBLIC ROUTES */}
      <Route
        path="/auth/instagram/callback"
        element={
          <LazyRoute>
            <InstagramCallback />
          </LazyRoute>
        }
      />
      <Route
        path="/:userType"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/cadastro"
        element={
          <PublicRoute>
            <SignUp />
          </PublicRoute>
        }
      />
      <Route
        path="/efetivar/:instance_key"
        element={
          <PublicRoute>
            <LazyRoute>
              <AccountEfetivation />
            </LazyRoute>
          </PublicRoute>
        }
      />
      <Route
        path="/efetivar/sucesso"
        element={
          <PublicRoute>
            <LazyRoute>
              <SucessScreen />
            </LazyRoute>
          </PublicRoute>
        }
      />
      <Route
        path="/cadastro/:hash"
        element={
          <PublicRoute>
            <SignUp />
          </PublicRoute>
        }
      />

      {/* ThankYou page is NOT wrapped in PrivateRoute to avoid race condition during signup. */}
      <Route
        path="/obrigado"
        element={
          <LazyRoute>
            <ThankYou />
          </LazyRoute>
        }
      />

      {/* CompleteProfile page - for users who need to add phone number */}
      <Route
        path="/completar-cadastro"
        element={
          <LazyRoute>
            <CompleteProfile />
          </LazyRoute>
        }
      />

      <Route
        path="/vendas/cadastro"
        element={
          <PublicRoute>
            <LazyRoute>
              <SignUpSales />
            </LazyRoute>
          </PublicRoute>
        }
      />
      <Route
        path="/recuperacao"
        element={
          <PublicRoute>
            <RecoveryRequest />
          </PublicRoute>
        }
      />
      <Route
        path="/recuperacao/:hash"
        element={
          <PublicRoute>
            <RecoveryNewPassword />
          </PublicRoute>
        }
      />
      {/* Gate de plano ativo obrigatório - ver needsPlan em PrivateRoute */}
      <Route
        path="/escolher-plano"
        element={
          <PrivateRoute>
            <LazyRoute>
              <ChoosePlan />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/finalizar-contratacao"
        element={
          <PrivateRoute>
            <LazyRoute>
              <PlanCheckout />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      {/* Demo provisioning - auto-create assistant from demo flow */}
      <Route
        path="/provisionar-demo"
        element={
          <PrivateRoute>
            <LazyRoute>
              <DemoProvisioning />
            </LazyRoute>
          </PrivateRoute>
        }
      />
      {/* PUBLIC DEMO — sem PublicRoute de propósito: quem já está logado também pode abrir a demo
          (PublicRoute mandaria pro dashboard). */}
      <Route
        path="/demonstracao"
        element={
          <LazyRoute>
            <DemoPage />
          </LazyRoute>
        }
      />
      {/* Tela terminal do funil da demo — "Próximo passo" no passo Assistentes cai aqui.
          NÃO usar "/obrigado": esse path já é a tela de pós-cadastro (acima) e venceria
          o match por vir primeiro na árvore. */}
      <Route
        path="/demonstracao/obrigado"
        element={
          <LazyRoute>
            <DemoThankYou />
          </LazyRoute>
        }
      />
      <Route path="*" element={<h1>404 Not found</h1>} />
    </RRDRoutes>
  );
};

// ============================================================================
// ROUTE GUARDS
// ============================================================================

function PublicRoute({ children }) {
  const { user, userSales, loading } = useContext(AuthContext);

  const getRecentRegistration = () => {
    try {
      const stored = sessionStorage.getItem("@sacgpt:newRegistration");
      if (stored) {
        const data = JSON.parse(stored);
        const age = Date.now() - data.timestamp;
        if (age < 5 * 60 * 1000) {
          return data;
        }
      }
    } catch (e) {
      // Silently fail
    }
    return null;
  };

  const recentRegistration = getRecentRegistration();

  if (loading) {
    return <PageLoader />;
  }

  if (userSales && userSales.userType === "sales") {
    return <Navigate to="/vendas/dashboard" />;
  }

  if (user) {
    if (user.userType === "admin") return <Navigate to="/admin/dashboard" />;
    if (user.userType === "partner" || user.userType === "partnerUser") {
      if (recentRegistration) {
        return (
          <Navigate
            to="/obrigado"
            state={{ partnerName: recentRegistration.partnerName, isNewRegistration: true }}
          />
        );
      }
      // Check if user came from demo flow
      if (getDemoAssistantData()) {
        return <Navigate to="/provisionar-demo" />;
      }
      return <Navigate to="/assistants" />;
    }
  }

  return children;
}

function PrivateRoute({ children }) {
  const { user, userSales, loading, updatePhoneNumber } = useContext(AuthContext);
  const location = useLocation();
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => setMenuOpen(false), [location.pathname, location.search]);

  const isSales = location.pathname.startsWith("/vendas");

  // Check if user needs phone number
  useEffect(() => {
    if (!loading && user && user.userType === "partner" && !isSales) {
      const needsPhone =
        !user.contactNumber || user.contactNumber === "" || user.contactNumber === null;
      if (needsPhone) {
        setShowPhoneModal(true);
      }
    }
  }, [loading, user, isSales]);

  const handlePhoneSubmit = async (phoneNumber) => {
    try {
      await updatePhoneNumber(phoneNumber);
      setShowPhoneModal(false);
    } catch (error) {
      throw error;
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  if (isSales) {
    if (!userSales || userSales.userType !== "sales") return <Navigate to="/" />;
  } else {
    if (!user) return <Navigate to="/" />;
  }

  // For partner users, render MainSidebar and TopBar at this level so they persist across page changes
  // This enables smooth CSS animations when navigating between dashboard and assistant pages
  // TopBar now uses NotificationContext so it doesn't need props from AuthorizedLayout
  // Exclude FlowEditor page from sidebar (but keep topbar)
  // Exclude immersive pages (entrevista, provisionar-demo) from BOTH topbar and
  // sidebar — são telas de onboarding pré-painel; o layout logado (topbar/sidebar)
  // não deve aparecer nelas. A tela é imersiva por design.
  const isFlowEditorPage = location.pathname.startsWith("/flows/editor");
  const isPlanGatePage =
    location.pathname.startsWith("/escolher-plano") ||
    location.pathname.startsWith("/finalizar-contratacao");
  const isImmersivePage =
    location.pathname.startsWith("/entrevista") ||
    location.pathname.startsWith("/provisionar-demo") ||
    isPlanGatePage;
  const shouldRenderPersistentLayout =
    !isSales &&
    user &&
    (user.userType === "partner" || user.userType === "partnerUser") &&
    !isImmersivePage;
  const shouldRenderSidebar = shouldRenderPersistentLayout && !isFlowEditorPage;

  // If phone modal is showing, don't render children
  if (showPhoneModal) {
    return <PhoneNumberModal open={showPhoneModal} onSubmit={handlePhoneSubmit} />;
  }

  // Gate de plano ativo obrigatório (contas criadas a partir do lançamento
  // desse gate, ver PLAN_GATE_LAUNCH_AT no backend) — telefone é resolvido
  // primeiro, plano depois. Não redireciona se já está nas próprias telas
  // do gate, senão vira loop.
  const needsPlan =
    !isSales &&
    user &&
    (user.userType === "partner" || user.userType === "partnerUser") &&
    user.activePlan === false &&
    !isPlanGatePage;
  if (needsPlan) {
    return <Navigate to="/escolher-plano" />;
  }

  return (
    <SidebarContext.Provider
      value={{
        sidebarRenderedByParent: shouldRenderSidebar,
      }}
    >
      {shouldRenderPersistentLayout && <PartnerTheme />}
      {shouldRenderSidebar && (
        <MainSidebar menuOpen={menuOpen} toggleMenu={() => setMenuOpen(false)} />
      )}
      {shouldRenderPersistentLayout && (
        <MainTopbar
          mainSidebarVisible={menuOpen}
          setMainSidebarVisible={shouldRenderSidebar ? setMenuOpen : undefined}
        />
      )}
      {children}
    </SidebarContext.Provider>
  );
}

function AdminRoute({ children }) {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return <PageLoader />;
  }

  if (!user || user.userType !== "admin") {
    return <Navigate to="/" />;
  }

  // Render AdminSidebar and MainTopbar for admin users (same pattern as PrivateRoute for partners)
  return (
    <SidebarContext.Provider
      value={{
        sidebarRenderedByParent: true,
      }}
    >
      <AdminSidebar />
      <MainTopbar />
      {children}
    </SidebarContext.Provider>
  );
}

function SalesRoute({ children }) {
  const { userSales, loading } = useContext(AuthContext);

  if (loading) {
    return <PageLoader />;
  }

  if (!userSales || userSales.userType !== "sales") {
    return <Navigate to="/" />;
  }

  return children;
}
